import BottomCta from "../core/bottom-cta";
import MagnifyingGlassIcon from "@heroicons/react/20/solid/MagnifyingGlassIcon";
import Footer from "../layout/footer";
import FAQHelpChannel from "./faq-channel";
import FAQSection from "./faq-section";
import Image from "next/image";
import SearchNotFoundImage from "@public/no-result.png";
import dynamic from "next/dynamic";
import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Trans, t } from "@lingui/macro";
import { useEffect, useRef, useState } from "react";
import { faqDetails } from "./faq-details";
import { useRouter } from "next/router";

const Header = dynamic(() => import("../layout/header"));

const FAQPage: React.FunctionComponent = () => {
  const [canRender, setCanRender] = useState(false);
  const [filteredResults, setFilteredResults] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const searchPlaceholder = t`Search FAQ?`;
  const router = useRouter();
  const { query } = router;
  const hideHeader = useRef(false);

  useEffect(() => {
    if (!router.isReady) {
      setCanRender(false);
    } else {
      if (query.m === "true") {
        hideHeader.current = true;
      }
      setCanRender(true);
    }
  }, [router.isReady]);

  const searchItems = (searchValue) => {
    if (searchValue !== "") {
      const filteredGeneralData = faqDetails[0].filter((item) =>
        Object.values(item)
          .join(",")
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      );
      const filteredAccPrefData = faqDetails[1].filter((item) =>
        Object.values(item)
          .join(",")
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      );
      const filteredUserProfileData = faqDetails[2].filter((item) =>
        Object.values(item)
          .join(",")
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      );
      const filteredNotiData = faqDetails[3].filter((item) =>
        Object.values(item)
          .join(",")
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      );
      const filteredCommunityData = faqDetails[4].filter((item) =>
        Object.values(item)
          .join(",")
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      );
      const filteredFypData = faqDetails[5].filter((item) =>
        Object.values(item)
          .join(",")
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      );
      const result = filteredGeneralData.concat(
        filteredAccPrefData,
        filteredUserProfileData,
        filteredNotiData,
        filteredCommunityData,
        filteredFypData
      );
      setSearchInput(searchValue);
      setFilteredResults(result);
    } else {
      setSearchInput(searchValue);
      setFilteredResults(faqDetails);
    }
  };

  const filteredResultData = (keyString) => {
    const result = filteredResults.map(
      (result) =>
        result.key.includes(keyString) && (
          <Disclosure key={result.key + Math.random()}>
            {({ open }) => (
              <>
                <Disclosure.Button className="flex w-full justify-between border-b-2 border-off-white py-2 text-left font-sen text-text-darkest">
                  <span className="w-72">{result.title}</span>
                  <ChevronDownIcon
                    className={`${
                      open ? "rotate-180 transform" : ""
                    } h-5 w-5 text-primary`}
                  />
                </Disclosure.Button>
                <Disclosure.Panel className="px-4 pt-4 pb-2 font-sen text-text-medium">
                  {result.answer}
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        )
    );
    return result;
  };

  const resultNotFound = (keyString) => {
    const result = filteredResults.filter((res) => res.key.includes(keyString))
      .length == 0 && (
      <p className="mt-4 pl-2 font-sen text-text-medium">
        <Trans>Result Not Found</Trans>
      </p>
    );
    return result;
  };

  return (
    <>
      <div className="min-h-screen flex-1 overflow-hidden">
        {canRender && !hideHeader.current && <Header showNewsStacks={false} />}
        <div className="mx-auto mt-10 max-w-7xl px-6">
          <div className="flex items-center justify-between rounded-2xl bg-off-white py-4 px-4">
            <Trans
              id={searchPlaceholder}
              render={({ translation }) => (
                <input
                  className="w-full bg-off-white font-sen text-text-darkest md:text-lg"
                  type="text"
                  name="search"
                  value={searchInput}
                  onChange={(e) => searchItems(e.target.value)}
                  placeholder={translation as string}
                />
              )}
            />
            <MagnifyingGlassIcon
              className="h-5 w-5 cursor-pointer text-primary3"
              aria-hidden="true"
            />
          </div>
        </div>
        <div className="mx-auto max-w-7xl">
          <p className="mt-10 px-6 pb-6 font-poppins text-3xl font-bold text-primary">
            <Trans>Lumi Help Centre</Trans>
          </p>
        </div>
        {searchInput.length > 1 && filteredResults.length == 0 ? (
          <div className="flex flex-col items-center space-y-4">
            <Image src={SearchNotFoundImage} alt="search-not-found" />
            <p className="px-3 pt-4 text-center font-poppins font-bold text-text-medium md:text-2xl">
              <Trans>We couldn't find anything on </Trans>{" "}
              <span className="text-primary">{`"${searchInput}"`}</span>
            </p>
            <p className="font-sen text-text-medium">
              <Trans>Search something else?</Trans>
            </p>
          </div>
        ) : searchInput.length > 1 && filteredResults.length > 0 ? (
          <div className="mx-auto max-w-7xl grid-cols-3 gap-4 px-6 md:grid">
            {/* <div className="w-full pt-16">
              <p className="pl-2 font-poppins text-xl font-bold text-text-darkest">
                <Trans>🔑 Creating a new account</Trans>
              </p>
              {resultNotFound("new-account")}
              <div className="mx-auto w-full max-w-md rounded-2xl bg-white p-2">
                {filteredResultData("new-account")}
              </div>
            </div>
            <div className="w-full pt-16">
              <p className="pl-2 font-poppins text-xl font-bold text-text-darkest">
                <Trans>👤 Returning users</Trans>
              </p>
              {resultNotFound("returning-users")}
              <div className="mx-auto w-full max-w-md rounded-2xl bg-white p-2">
                {filteredResultData("returning-users")}
              </div>
            </div>
            <div className="w-full pt-16">
              <p className="pl-2 font-poppins text-xl font-bold text-text-darkest">
                <Trans>🔗 Connecting My Account</Trans>
              </p>
              {resultNotFound("connecting-my-account")}
              <div className="mx-auto w-full max-w-md rounded-2xl bg-white p-2">
                {filteredResultData("connecting-my-account")}
              </div>
            </div> */}
            <div className="w-full pt-16">
              <p className="pl-2 font-poppins text-xl font-bold text-text-darkest">
                <Trans>⚙️ General</Trans>
              </p>
              {resultNotFound("general")}
              <div className="mx-auto w-full max-w-md rounded-2xl bg-white p-2">
                {filteredResultData("general")}
              </div>
            </div>
            <div className="w-full pt-16">
              <p className="pl-2 font-poppins text-xl font-bold text-text-darkest">
                <Trans>🔒 Account Preferences</Trans>
              </p>
              {resultNotFound("account-preferences")}
              <div className="mx-auto w-full max-w-md rounded-2xl bg-white p-2">
                {filteredResultData("account-preferences")}
              </div>
            </div>
            <div className="w-full pt-16">
              <p className="pl-2 font-poppins text-xl font-bold text-text-darkest">
                <Trans>🔔 Notifications</Trans>
              </p>
              {resultNotFound("notification")}
              <div className="mx-auto w-full max-w-md rounded-2xl bg-white p-2">
                {filteredResultData("notification")}
              </div>
            </div>
            <div className="w-full pt-16">
              <p className="pl-2 font-poppins text-xl font-bold text-text-darkest">
                <Trans>💬 Chat, Comments, and Community</Trans>
              </p>
              {resultNotFound("community")}
              <div className="mx-auto w-full max-w-md rounded-2xl bg-white p-2">
                {filteredResultData("community")}
              </div>
            </div>
            <div className="w-full pt-16">
              <p className="pl-2 font-poppins text-xl font-bold text-text-darkest">
                <Trans>🏠 For You tab</Trans>
              </p>
              {resultNotFound("for-you")}
              <div className="mx-auto w-full max-w-md rounded-2xl bg-white p-2">
                {filteredResultData("for-you")}
              </div>
            </div>
            <div className="w-full pt-16">
              <p className="pl-2 font-poppins text-xl font-bold text-text-darkest">
                <Trans>🤖 Lumi AI</Trans>
              </p>
              {resultNotFound("lumi-ai")}
              <div className="mx-auto w-full max-w-md rounded-2xl bg-white p-2">
                {filteredResultData("lumi-ai")}
              </div>
            </div>
          </div>
        ) : (
          <FAQSection />
        )}
        <FAQHelpChannel />
        <div>
          <BottomCta />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default FAQPage;
